import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  X,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from '@/hooks/useTranslation';
import { useDockLayoutStore } from '@/stores/useDockLayoutStore';
import { useFloatingClearance } from '@/hooks/useFloatingClearance';
import {
  selectAllSynthJobs,
  useSynthJobsStore,
  type SynthJob,
} from '@/stores/useSynthJobsStore';
import { noteRoute } from '@/config/noteRoute';

/**
 * Persistent floating affordance for in-progress + recently-completed
 * SOAP synth jobs. Mounted at app shell so the doctor sees their notes
 * being generated regardless of route.
 *
 * Collapsed: a single pill in the bottom-right showing
 *   "{N} note{s} generating · last status".
 * Expanded: a popover listing every active/completed job with per-job
 *   mini-progress bar, status line, and a one-click "Open" button on
 *   completion.
 *
 * Completed/failed jobs auto-stay for ~60 s after finish so the doctor
 * doesn't lose the result on a quick route change. A manual dismiss
 * button is also there.
 */

const AUTO_DISMISS_MS = 60_000;

export const SynthBackgroundPill: React.FC = () => {
  const { isRTL } = useTranslation();
  const navigate = useNavigate();
  // useShallow prevents the "getSnapshot should be cached" warning + infinite
  // render loop. `selectAllSynthJobs` returns `Object.values(...).sort(...)` —
  // a fresh array reference every call, which fails React 18's
  // useSyncExternalStore cached-snapshot check and triggers a re-render that
  // recomputes the selector, ad infinitum. Shallow-comparing the array's
  // members short-circuits that.
  const jobs = useSynthJobsStore(useShallow(selectAllSynthJobs));
  const dismiss = useSynthJobsStore((s) => s.dismiss);
  const [expanded, setExpanded] = useState(false);

  // This card shares the bottom-right corner with the Save & sign bar (below)
  // and the MaieLauncherPill (above). It LIFTS above the action bar + fixed
  // docks itself, and REPORTS its resulting (lifted) rect to `synthCard` so the
  // launcher can stack above it. `includeSynthCard: false` so it doesn't try to
  // clear its own reported rect.
  const setSynthCard = useDockLayoutStore((s) => s.setSynthCard);
  const { ref: cardRef, lift } = useFloatingClearance<HTMLDivElement>({
    report: setSynthCard,
    includeSynthCard: false,
  });

  // Auto-dismiss completed/failed jobs after the grace window.
  React.useEffect(() => {
    if (jobs.length === 0) return;
    const timers = jobs
      .filter((j) => j.status !== 'running' && j.finishedAt)
      .map((j) => {
        const remaining = AUTO_DISMISS_MS - (Date.now() - (j.finishedAt ?? 0));
        if (remaining <= 0) {
          dismiss(j.id);
          return null;
        }
        const t = setTimeout(() => dismiss(j.id), remaining);
        return t;
      });
    return () => {
      for (const t of timers) if (t) clearTimeout(t);
    };
  }, [jobs, dismiss]);

  if (jobs.length === 0) return null;

  const running = jobs.filter((j) => j.status === 'running');
  const failed = jobs.filter((j) => j.status === 'failed');
  const headline =
    running.length > 0
      ? isRTL
        ? `${running.length} ملاحظة قيد التوليد`
        : `${running.length} note${running.length === 1 ? '' : 's'} generating`
      : failed.length > 0
        ? isRTL
          ? 'تعذّر إنشاء ملاحظة'
          : 'Note failed'
        : isRTL
          ? 'الملاحظة جاهزة'
          : 'Note ready';
  const subline =
    running[0]?.statusLabel ??
    (running.length > 0
      ? isRTL
        ? 'جاري المعالجة…'
        : 'Working on it…'
      : '');

  return (
    <div
      ref={cardRef}
      className="fixed bottom-4 end-4 z-40 w-[320px] sm:w-[360px]"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{
        marginBottom: 'env(safe-area-inset-bottom, 0)',
        transform: lift > 0 ? `translateY(-${lift}px)` : undefined,
      }}
    >
      {expanded && (
        <div
          className="mb-2 rounded-xl shadow-xl overflow-hidden"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            border: '1px solid var(--color-border-primary)',
          }}
        >
          <div
            className="px-3 py-2 flex items-center justify-between"
            style={{ borderBottom: '1px solid var(--color-border-primary)' }}
          >
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {isRTL ? 'الملاحظات قيد المعالجة' : 'Notes in progress'}
            </span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="p-1 rounded"
              aria-label={isRTL ? 'طي' : 'Collapse'}
            >
              <ChevronDown
                className="w-4 h-4"
                style={{ color: 'var(--color-text-tertiary)' }}
              />
            </button>
          </div>
          <ul className="max-h-[60vh] overflow-y-auto">
            {jobs.map((job) => (
              <SynthJobRow
                key={job.id}
                job={job}
                isRTL={isRTL}
                onOpen={() => {
                  if (job.recordId) navigate(noteRoute(job.recordId));
                }}
                onDismiss={() => dismiss(job.id)}
              />
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl shadow-lg transition-colors"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          border: `1px solid ${failed.length > 0 ? 'var(--color-error)' : 'var(--color-border-primary)'}`,
        }}
      >
        {running.length > 0 ? (
          <Loader2
            className="w-5 h-5 animate-spin flex-shrink-0"
            style={{ color: 'var(--color-primary-600)' }}
          />
        ) : failed.length > 0 ? (
          <AlertCircle
            className="w-5 h-5 flex-shrink-0"
            style={{ color: 'var(--color-error)' }}
          />
        ) : (
          <Check
            className="w-5 h-5 flex-shrink-0"
            style={{ color: 'var(--color-success)' }}
          />
        )}
        <div className="flex-1 min-w-0 text-start">
          <div
            className="text-sm font-semibold truncate"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {headline}
          </div>
          {subline && (
            <div
              className="text-[11px] truncate"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {subline}
            </div>
          )}
        </div>
        {expanded ? (
          <ChevronDown
            className="w-4 h-4 flex-shrink-0"
            style={{ color: 'var(--color-text-tertiary)' }}
          />
        ) : (
          <ChevronUp
            className="w-4 h-4 flex-shrink-0"
            style={{ color: 'var(--color-text-tertiary)' }}
          />
        )}
      </button>
    </div>
  );
};

interface SynthJobRowProps {
  job: SynthJob;
  isRTL: boolean;
  onOpen: () => void;
  onDismiss: () => void;
}

const SynthJobRow: React.FC<SynthJobRowProps> = ({
  job,
  isRTL,
  onOpen,
  onDismiss,
}) => {
  const stages = Object.values(job.stages);
  const total = stages.length || 6; // 6 = canonical pipeline length
  const done = stages.filter((s) => s.status === 'done').length;
  const elapsed = Math.max(0, Math.round((Date.now() - job.startedAt) / 1000));

  return (
    <li
      className="px-3 py-2.5"
      style={{ borderBottom: '1px solid var(--color-border-primary)' }}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-semibold truncate"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {job.patientName ||
              (isRTL ? 'بدون مريض' : 'Unassigned encounter')}
          </div>
          <div
            className="text-[11px] truncate"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {job.kind === 'regenerate'
              ? isRTL
                ? 'إعادة توليد'
                : 'Regenerating'
              : isRTL
                ? 'إنشاء ملاحظة'
                : 'Generating note'}
            {' · '}
            {elapsed}s
            {job.transcriptIsRefined && (
              <span
                className="inline-flex items-center gap-1 ms-2 px-1 py-0.5 rounded text-[10px]"
                style={{
                  backgroundColor:
                    'color-mix(in srgb, var(--color-success) 18%, transparent)',
                  color: 'var(--color-success)',
                }}
              >
                <Sparkles className="w-2.5 h-2.5" />
                {isRTL ? 'محسّن' : 'Refined'}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 rounded -mt-1 -me-1"
          aria-label={isRTL ? 'إخفاء' : 'Dismiss'}
        >
          <X
            className="w-3.5 h-3.5"
            style={{ color: 'var(--color-text-tertiary)' }}
          />
        </button>
      </div>

      {job.status === 'running' && (
        <>
          <div
            className="h-1 w-full overflow-hidden rounded-full"
            style={{
              backgroundColor:
                'color-mix(in srgb, var(--color-primary-600) 12%, transparent)',
            }}
          >
            <div
              className="h-full transition-all"
              style={{
                width: `${(done / total) * 100}%`,
                backgroundColor: 'var(--color-primary-600)',
                transitionDuration: '300ms',
              }}
            />
          </div>
          {job.statusLabel && (
            <div
              className="text-[11px] mt-1"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {job.statusLabel}
            </div>
          )}
        </>
      )}

      {job.status === 'completed' && (
        <button
          type="button"
          onClick={onOpen}
          disabled={!job.recordId}
          className="mt-1 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: 'var(--color-primary-600)' }}
        >
          <Sparkles className="w-3 h-3" />
          {isRTL ? 'فتح الملاحظة' : 'Open note'}
        </button>
      )}

      {job.status === 'failed' && (
        <div
          className="text-[11px] rounded px-2 py-1 mt-1"
          style={{
            backgroundColor:
              'color-mix(in srgb, var(--color-error) 10%, transparent)',
            color: 'var(--color-error)',
          }}
        >
          {job.errorMessage ?? (isRTL ? 'فشل' : 'Failed')}
        </div>
      )}
    </li>
  );
};
